import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { UserState, Users } from './users.interface';

// Thunks para Usuarios
import * as thunks from './usersThunks';
import { APIResponse } from '@/types/custom-http';


const initialState: UserState = {
  users: [],
  selectedUser: null,
  loading: false,
  error: null,
};



// Slice
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearUsers: (state) => {
      state.users = [];
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    clearUsersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Obtener todos los usuarios
      .addCase(thunks.fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchUsers.fulfilled, (state, action: PayloadAction<APIResponse<Users[]>>) => {
        state.loading = false;
        if(action.payload.data){
          state.users = action.payload.data;
        }
      })
      .addCase(thunks.fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Obtener un usuarios específico por ID
      .addCase(thunks.fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchUserById.fulfilled, (state, action: PayloadAction<APIResponse<Users>>) => {
        state.loading = false;
        if(action.payload.data){
          state.selectedUser = action.payload.data;
        }
      })
      .addCase(thunks.fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Crear un nuevo Usuario
      .addCase(thunks.createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createUser.fulfilled, (state, action: PayloadAction<APIResponse<Users>>) => {
        state.loading = false;
        if(action.payload.data){
          state.users.push(action.payload.data);
        }
      })
      .addCase(thunks.createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Actualizar datos de un Usuario
      .addCase(thunks.updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateUser.fulfilled, (state, action: PayloadAction<APIResponse<Users>>) => {
        state.loading = false;
        const index = state.users.findIndex(user => user.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.users[index] = action.payload.data;
        }
        if (state.selectedUser && state.selectedUser.id === action.payload.data?.id) {
          state.selectedUser = action.payload.data;
        }
      })
      .addCase(thunks.updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Eliminar un usuario
      .addCase(thunks.deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        const deletedUserId = action.payload;
        
        state.users = state.users.filter(user => user.id !== deletedUserId);
        
        if (state.selectedUser && state.selectedUser.id === deletedUserId) {
          state.selectedUser = null;
        }
      })
      .addCase(thunks.deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })
      
  },
});

// Selectors
export const selectAllUsers = (state: RootState) => state.users.users;
export const selectUserById = (state: RootState, uuid: string) => state.users.users.find(user => user.id === uuid);
export const selectUsersLoading = (state: RootState) => state.users.loading;
export const selectUsersError = (state: RootState) => state.users.error;

// Actions
export const { clearUsers, clearSelectedUser,  clearUsersError} = usersSlice.actions;

export default usersSlice.reducer;